// ─────────────────────────────────────────
// Groups.js — browse, search, and join groups
// ─────────────────────────────────────────
// Two sections:
//   "My Groups"    — clubs the logged-in user belongs to
//   "Public Groups" — open groups anyone can browse and join
//
// Top bar has a search field and a "Create a Group" button.

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import Nav from '../components/Nav'
import { useIsMobile } from '../hooks/useIsMobile'

export default function Groups({ session }) {
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  const [myGroups, setMyGroups] = useState([])
  const [publicGroups, setPublicGroups] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchGroups()
  }, [])

  // Debounce the search — wait 300ms after typing stops before querying
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }
    const timer = setTimeout(() => searchGroups(searchQuery.trim()), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  async function fetchGroups() {
    // Flat separate queries — embedded joins silently return null for private groups
    const [{ data: memberRows }, { data: publicRows }] = await Promise.all([
      supabase.from('group_members').select('group_id, role').eq('user_id', session.user.id),
      supabase.from('groups').select('*').eq('privacy', 'public').order('member_count', { ascending: false }).limit(30)
    ])

    const roleMap = {}
    ;(memberRows || []).forEach(r => { roleMap[r.group_id] = r.role })
    const myGroupIds = Object.keys(roleMap)

    // Fetch the actual group data directly — works for both public and private groups
    let myGroupList = []
    if (myGroupIds.length > 0) {
      const { data: myGroupRows } = await supabase
        .from('groups').select('*').in('id', myGroupIds)
      myGroupList = (myGroupRows || []).map(g => ({ ...g, myRole: roleMap[g.id] }))
    }

    const publicList = publicRows || []

    // Get all unique group IDs across both lists
    const allGroupIds = [...new Set([...myGroupList.map(g => g.id), ...publicList.map(g => g.id)])]

    // Fetch real member counts (includes all roles) in one query
    const { data: countRows } = await supabase
      .from('group_members')
      .select('group_id')
      .in('group_id', allGroupIds)

    const countMap = {}
    ;(countRows || []).forEach(r => {
      countMap[r.group_id] = (countMap[r.group_id] || 0) + 1
    })

    const withCount = g => ({ ...g, member_count: countMap[g.id] ?? g.member_count ?? 0 })

    setMyGroups(myGroupList.map(withCount))
    setPublicGroups(publicList.map(withCount))
    setLoading(false)
  }

  async function searchGroups(query) {
    setSearching(true)
    const { data } = await supabase
      .from('groups')
      .select('*')
      .ilike('name', `%${query}%`)
      .limit(20)

    const results = data || []
    if (results.length > 0) {
      const ids = results.map(g => g.id)
      const { data: countRows } = await supabase.from('group_members').select('group_id').in('group_id', ids)
      const countMap = {}
      ;(countRows || []).forEach(r => { countMap[r.group_id] = (countMap[r.group_id] || 0) + 1 })
      setSearchResults(results.map(g => ({ ...g, member_count: countMap[g.id] ?? g.member_count ?? 0 })))
    } else {
      setSearchResults([])
    }
    setSearching(false)
  }

  // Which groups to show in the public section — search results override the default list
  const displayedPublic = searchQuery.trim() ? searchResults : publicGroups

  // ── RENDER ───────────────────────────────────────────────────

  return (
    <div style={{ ...styles.page, paddingBottom: isMobile ? 72 : 0 }}>

      {/* ── TOP BAR ── */}
      <div style={styles.topbar}>
        <div style={styles.logo}>BREAKIE</div>
        <Link to="/groups/create" style={styles.createBtn}>＋ Create</Link>
      </div>

      <Nav />

      <div style={styles.body}>

        {/* ── SEARCH BAR ── */}
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            style={styles.searchInput}
            placeholder="Search groups by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery.length > 0 && (
            <button style={styles.searchClear} onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>

        {/* ── MY GROUPS ── */}
        {/* Only shown when not actively searching */}
        {!searchQuery && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionTitle}>My Groups</div>
              <Link to="/groups/create" style={styles.sectionCreateLink}>＋ New</Link>
            </div>

            {loading && <div style={styles.empty}>Loading...</div>}

            {!loading && myGroups.length === 0 && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>👥</div>
                <div style={styles.emptyTitle}>You're not in any groups yet</div>
                <div style={styles.emptyText}>Create one or join a public group below.</div>
                <Link to="/groups/create" style={styles.emptyBtn}>Create a Group →</Link>
              </div>
            )}

            {myGroups.map(group => (
              <GroupCard key={group.id} group={group} showRole navigate={navigate} />
            ))}
          </div>
        )}

        {/* ── PUBLIC GROUPS / SEARCH RESULTS ── */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>
              {searchQuery ? `Results for "${searchQuery}"` : 'Public Groups'}
            </div>
            {searching && <div style={{ fontSize: 12, color: '#6b7c6e' }}>Searching...</div>}
          </div>

          {!loading && displayedPublic.length === 0 && (
            <div style={styles.emptyState}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
              <div style={styles.emptyTitle}>
                {searchQuery ? 'No groups found' : 'No public groups yet'}
              </div>
              <div style={styles.emptyText}>
                {searchQuery ? 'Try a different name, or create this group.' : 'Be the first to create one.'}
              </div>
              <Link to="/groups/create" style={styles.emptyBtn}>Create a Group →</Link>
            </div>
          )}

          {displayedPublic.map(group => (
            <GroupCard key={group.id} group={group} navigate={navigate} />
          ))}
        </div>

      </div>
    </div>
  )
}

// ── GROUP CARD ───────────────────────────────────────────────
// Reusable card used in both sections

function GroupCard({ group, showRole, navigate }) {
  const isAdmin = showRole && group.myRole === 'admin'
  return (
    <div style={cardStyles.card} onClick={() => navigate(`/groups/${group.id}`)}>
      <div style={cardStyles.icon}>{group.icon_emoji || '⛳'}</div>
      <div style={cardStyles.info}>
        <div style={cardStyles.name}>{group.name}</div>
        {group.description && (
          <div style={cardStyles.desc}>{group.description}</div>
        )}
        <div style={cardStyles.meta}>
          <span>{group.member_count || 0} member{group.member_count !== 1 ? 's' : ''}</span>
          <span style={cardStyles.dot}>·</span>
          <span style={{
            color: group.privacy === 'public' ? '#1a7a3c' : '#6b7c6e'
          }}>
            {group.privacy === 'public' ? '🌐 Public' : '🔒 Private'}
          </span>
          {isAdmin && (
            <>
              <span style={cardStyles.dot}>·</span>
              <span style={{ color: '#c8a835', fontWeight: 700 }}>Admin</span>
            </>
          )}
        </div>
      </div>
      {isAdmin ? (
        <button
          style={cardStyles.editBtn}
          onClick={e => { e.stopPropagation(); navigate(`/groups/${group.id}?tab=edit`) }}
          title="Edit group"
        >
          ✏️
        </button>
      ) : (
        <div style={cardStyles.arrow}>›</div>
      )}
    </div>
  )
}

// ── STYLES ───────────────────────────────────────────────────

const styles = {
  page: { minHeight: '100vh', background: '#f4f7f5', fontFamily: "'Barlow', sans-serif" },

  topbar: {
    background: '#0e1f13', padding: '14px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    position: 'sticky', top: 0, zIndex: 10
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: '#c8f135', letterSpacing: 2
  },
  createBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none', borderRadius: 10,
    padding: '8px 16px', fontWeight: 800, fontSize: 13, cursor: 'pointer',
    textDecoration: 'none', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1
  },


  body: { maxWidth: 600, margin: '0 auto', padding: '16px 16px 40px' },

  // ── Search bar ────────────────────────────────────────
  searchWrapper: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 14, padding: '10px 14px', marginBottom: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  },
  searchIcon: { fontSize: 16, flexShrink: 0 },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: 15,
    color: '#0e1f13', background: 'transparent', fontFamily: "'Barlow', sans-serif"
  },
  searchClear: {
    background: '#f4f7f5', border: 'none', borderRadius: '50%',
    width: 22, height: 22, fontSize: 11, cursor: 'pointer',
    color: '#6b7c6e', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },

  // ── Section ───────────────────────────────────────────
  section: {
    background: 'white', borderRadius: 18, marginBottom: 14,
    overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  sectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 18px 12px', borderBottom: '1px solid #f0f4f1'
  },
  sectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 15, color: '#0e1f13',
    letterSpacing: 0.8, textTransform: 'uppercase'
  },
  sectionCreateLink: {
    fontSize: 12, fontWeight: 700, color: '#1a7a3c',
    textDecoration: 'none', letterSpacing: 0.3
  },

  empty: { padding: '24px', textAlign: 'center', color: '#6b7c6e', fontSize: 13 },
  emptyState: { padding: '28px 20px', textAlign: 'center' },
  emptyTitle: { fontWeight: 700, fontSize: 15, color: '#0e1f13', marginBottom: 6 },
  emptyText: { fontSize: 13, color: '#6b7c6e', marginBottom: 18 },
  emptyBtn: {
    display: 'inline-block', background: '#c8f135', color: '#0e1f13',
    border: 'none', borderRadius: 10, padding: '10px 20px',
    fontSize: 13, fontWeight: 800, textDecoration: 'none',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  }
}

const cardStyles = {
  card: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '14px 18px', cursor: 'pointer',
    borderBottom: '1px solid #f0f4f1',
    transition: 'background 0.1s'
  },
  icon: {
    width: 48, height: 48, borderRadius: 14,
    background: 'linear-gradient(135deg, #1a7a3c, #0e1f13)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 24, flexShrink: 0
  },
  info: { flex: 1, minWidth: 0 },
  name: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 16, color: '#0e1f13',
    marginBottom: 3, letterSpacing: 0.3
  },
  desc: {
    fontSize: 12, color: '#6b7c6e', marginBottom: 4,
    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'
  },
  meta: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6b7c6e' },
  dot: { opacity: 0.5 },
  arrow: { fontSize: 22, color: '#c8d5ca', flexShrink: 0, fontWeight: 300 },
  editBtn: {
    background: 'rgba(200,168,53,0.12)', border: 'none', borderRadius: 10,
    padding: '7px 10px', fontSize: 15, cursor: 'pointer', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }
}
